<template>
  <div :class="['quote-wizard', 'step-' + currentStepNumber]">
    <div v-if="wizardInProgress" v-show="asyncState !== 'pending'">
      <ol class="steps">
        <li :class="{ step: true, 'is-active': currentStepNumber === 1 }">
          Your Info
        </li>
        <li :class="{ step: true, 'is-active': currentStepNumber === 2 }">
          Your Vehicle
        </li>
        <li :class="{ step: true, 'is-active': currentStepNumber === 3 }">
          Your Drivers
        </li>
        <li :class="{ step: true, 'is-active': currentStepNumber === 4 }">
          Your Coverage
        </li>
        <li :class="{ step: true, 'is-active': currentStepNumber === 5 }">
          Your Quote
        </li>
        <li
          v-if="currentStepNumber === 6"
          :class="{ step: true, 'is-active': currentStepNumber === 6 }"
        >
          Payment
        </li>
      </ol>

      <div class="form-wrapper">
        <div class="step0-text" v-if="currentStepNumber === 0">Loading...</div>
        <keep-alive>
          <component
            ref="currentStep"
            :is="currentStep"
            @updateAsyncState="updateAsyncState"
            :wizard-data="form"
            :currentStepNumber="currentStepNumber"
            :isLastStep="isLastStep"
            @goBack="goBack"
            @nextButtonAction="nextButtonAction"
            @saveMyQuote="saveMyQuote"
          ></component>
        </keep-alive>
      </div>
    </div>

    <div v-else class="form-wrapper"><QuoteThankYou /></div>

    <div class="loading-wrapper" v-if="asyncState === 'pending'">
      <div class="loader"><p>Please wait, we're hitting our servers!</p></div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import QuoteInfo from './QuoteInfo';
import QuoteVehicle from './QuoteVehicle';
import QuoteDrivers from './QuoteDrivers';
import QuoteCoverage from './QuoteCoverage';
import QuoteThankYou from './QuoteThankYou';
import QuotePayment from './QuotePayment';
import QuoteQuote from './QuoteQuote';
import Lienholders from './Lienholders';
import { mapGetters } from 'vuex';

export default {
  name: 'QuoteWizard',
  components: {
    QuoteInfo,
    QuoteVehicle,
    QuoteDrivers,
    QuoteCoverage,
    QuotePayment,
    Lienholders,
    QuoteThankYou,
    QuoteQuote
  },
  props: {
    currentStepNumber: {
      type: Number
    }
  },
  data: () => ({
    asyncState: null,
    steps: [
      'QuoteInfo',
      'QuoteVehicle',
      'QuoteDrivers',
      'QuoteCoverage',
      'QuoteQuote',
      'QuotePayment'
    ],
    form: {
      firstName: null,
      lastName: null,
      streetAddressOne: null,
      zipCode: null,
      isGarageSameAsMailing: null,
      garagingStreetAddressOne: null,
      garagingZipCode: null,
      garagingCity: null,
      dateOfBirth: null,
      phoneNumber: null,
      phoneType: null,
      hearAbout: null,
      dealer: null,
      bodilyInjury: null,
      propertyDamage: null,
      personalInjuryProtection: null,
      comprehensiveDeductible: null,
      collisionDeductible: null,
      uninsuredBodilyInjury: null,
      uninsuredPropertyDamage: null
    },
    subscription: null
  }),
  mounted: function() {
    if (!this.quoteResponse) {
      //updating quoteResponse if policies have values
      this.fetchPolicy();
    } else {
      this.goToStep(4);
      //updating quoteResponse if policies have no values (eg: handling reload from quote page)
      this.subscription = this.$store.subscribe((mutation, state) => {
        if (
          mutation.type === 'updateSessionStatus' &&
          !state.isLoggedIn &&
          !state.bindResponse
        ) {
          this.fetchPolicy();
        }
      });
    }
  },
  destroyed: function() {
    if (this.subscription) {
      this.subscription();
    }
    this.updateForm(null);
    this.updateQuoteRequest(null);
    this.updateBindResponse(null);
    this.updateQuoteResponse(null);
  },
  computed: {
    ...mapGetters([
      'quoteForm',
      'quoteResponse',
      'policies',
      'isLoggedIn',
      'user'
    ]),
    isLastStep() {
      return this.currentStepNumber === this.length;
    },
    wizardInProgress() {
      return this.currentStepNumber <= this.length;
    },
    length() {
      return this.steps.length;
    },
    currentStep() {
      return this.steps[this.currentStepNumber - 1];
    }
  },

  methods: {
    ...mapMutations([
      'updateBindResponse',
      'updateQuoteResponse',
      'updateQuoteRequest',
      'updateForm',
      'toggleLoader'
    ]),
    ...mapActions(['saveQuote', 'fetchQuote']),
    updateAsyncState(state) {
      this.asyncState = state;
    },
    updateStepNumber(newStep) {
      this.$emit('updateStep', newStep);
      setTimeout(() => {
        this.triggerPageView();
      }, 25);
    },
    triggerPageView() {
      let quotePath;
      if (process.env.VUE_APP_ENABLE_GOOGLE_ANALYTICS === 'true') {
        switch (this.currentStepNumber) {
          case 1:
            quotePath = 'quote-info-page';
            break;
          case 2:
            quotePath = 'quote-vehicles-page';
            break;
          case 3:
            quotePath = 'quote-drivers-page';
            break;
          case 4:
            quotePath = 'quote-coverage-page';
            break;
          case 5:
            quotePath = 'quote-quote-page';
            break;
          case 6:
            quotePath = 'quote-payment-page';
            break;
          case 7:
            quotePath = 'quote-thank-you-page';
            break;
        }
        gtag('config', process.env.VUE_APP_GA_ID, {
          page_title: this.$route.path + '/' + encodeURIComponent(quotePath),
          page_path: this.$route.path + '/' + encodeURIComponent(quotePath)
        });
        gtag('event', 'page_view', {
          page_title: this.$route.path + '/' + encodeURIComponent(quotePath),
          page_path: this.$route.path + '/' + encodeURIComponent(quotePath),
          send_to: process.env.VUE_APP_GTAG_ID
        });
      }
      return this.currentStepNumber;
    },
    submitOrder() {
      this.asyncState = 'pending';
      // postFormToDB(this.form).then(() => {
      this.asyncState = 'success';
      this.goNext(this.currentStep + 1);
      this.$emit('completedQuote', 'Welcome to Porsche Auto Insurance');
      // });
    },

    nextButtonAction() {
      let that = this;
      this.$refs.currentStep
        .submit()
        .then(stepData => {
          Object.assign(this.form, stepData);
          if (that.isLastStep) {
            this.scrollTop();
            this.submitOrder();
          } else {
            this.scrollTop();
            this.goNext();
          }
        })
        .catch(error => console.log(error));
    },

    saveMyQuote() {
      if (this.isLoggedIn) {
        let payload = {
          policyNumbers: [this.quoteResponse.policies[0].policyNumber],
          emailAddress: this.user.emailAddress
        };
        this.toggleLoader({
          loaderText: 'Loading...'
        });
        this.saveQuote(payload)
          .then(() => {
            this.toggleLoader();
            this.showSnackBar({
              type: 'is-success',
              message: 'Quote Saved Successfully',
              position: 'is-bottom'
            });
            this.$router.push('/');
          })
          .catch(() => {
            this.toggleLoader();
            this.showSnackBar({
              type: 'is-danger',
              message: '',
              position: 'is-bottom'
            });
            let message = document.querySelector('.snackbar > .text');
            message.innerHTML = `The system is unable to complete your request at this time.
            Please call <nobr><a style="text-decoration:underline;color:#fff" href="tel:1-800-400-4128">800-400-4128</a></nobr>
            to speak to a customer service concierge.`;
          });
      }
    },
    showSnackBar(options) {
      this.$snackbar.open(options);
    },
    gotoLookupQuotes() {
      this.$router.push('/account/lookup-quotes');
    },
    goBack() {
      this.scrollTop();
      if (this.currentStepNumber !== 1) {
        this.updateStepNumber(this.currentStepNumber - 1);
      }
    },
    goNext() {
      this.updateStepNumber(this.currentStepNumber + 1);
    },
    goToStep(stepNumber) {
      this.updateStepNumber(stepNumber);
    },

    scrollTop() {
      let container = document.getElementById('quote-container');
      setTimeout(() => {
        container.scrollIntoView(true);
      }, 100);
    },

    fetchPolicy() {
      if (
        this.$router.currentRoute.query.email &&
        this.$router.currentRoute.query.dob &&
        this.$router.currentRoute.query.zipcode
      ) {
        this.toggleLoader({
          loaderText: 'Please wait while we retrieve your information.'
        });
        let request = {
          emailAddress: this.$router.currentRoute.query.email,
          dob: this.$router.currentRoute.query.dob,
          zip: this.$router.currentRoute.query.zipcode
        };
        this.fetchQuote(request)
          .then(() => {
            this.toggleLoader();
            this.goToStep(1);
            if (this.subscription) {
              this.subscription();
            }
          })
          .catch(() => {
            this.goToStep(1);
            this.toggleLoader();
            if (this.subscription) {
              this.subscription();
            }
            this.$router.push('/');
            this.$snackbar.open({
              type: 'is-danger',
              message: `We were unable to locate your quote. Please click the button 'Start a new quote' to begin a new quote.`,
              position: 'is-bottom'
            });
          });
      } else {
        // TODO: revert changes here once dataMap is fixed;
        this.goToStep(1);
      }
    }
  }
};
</script>
<style lang="scss">
.button-right-align {
  display: flex;
  margin-top: 2rem;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.button-center-align {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  @media (max-width: 760px) {
    justify-content: center;
  }
}
.step0-text {
  text-align: center;
}
</style>
